import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Nav from './Nav';
import AuthModal from './AuthModal';
import Footer from './Footer';

const EXAMPLES = [
  { name: 'Your best friend', sub: 'The one who actually picks up' },
  { name: 'Your partner', sub: 'Obviously' },
  { name: 'Your ride or die', sub: 'No explanation needed' },
  { name: 'Your mentor', sub: 'The one who believed in you first' },
  { name: 'Your collaborator', sub: 'Built something great together' },
  { name: 'Your favorite artist', sub: 'Still hoping they follow back' },
  { name: 'Your sibling', sub: 'Love them or not, they make the list' },
  { name: 'That one person', sub: "You know who" },
];

function HomePage({ currentUser, currentUserProfile, onLogin }) {
  const [authOpen, setAuthOpen] = useState(false);

  return (
    <>
      <Nav currentUser={currentUser} currentUserProfile={currentUserProfile} />
      <div style={{ background: 'var(--bg)', minHeight: '100vh' }}>

        {/* Hero */}
        <div style={{ maxWidth: 680, margin: '0 auto', padding: '72px 20px 60px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {/* Logo: left edge aligns with tiles, tight to text */}
            <img src="/snughero.png" alt="Your Top 8" style={{ width: 'clamp(120px, 18vw, 200px)', height: 'auto', objectFit: 'contain', flexShrink: 0 }} />

            {/* Text + CTA */}
            <div style={{ flex: 1, minWidth: 240 }}>
              <h1 style={{
                fontSize: 'clamp(32px, 6vw, 52px)', fontWeight: 900,
                lineHeight: 1.1, margin: '0 0 16px', letterSpacing: '-1.5px',
              }}>
                Who's in your<br />
                <span style={{
                  background: 'linear-gradient(135deg, var(--accent), var(--accent-2))',
                  WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}>Top 8?</span>
              </h1>
              <p style={{ fontSize: 16, color: 'var(--muted)', lineHeight: 1.65, margin: '0 0 16px' }}>
                The eight people who matter most to you, right now. A social profile
                that actually says something. No algorithm, no wall, no messages, just
                the people you choose... and a notification when they change.
              </p>
              <p style={{ fontSize: 15, fontStyle: 'italic', color: 'var(--text)', lineHeight: 1.6, margin: '0 0 28px', opacity: 0.9 }}>
                Maybe what the world needs right now is a little dose of the social
                vulnerability that 2005 had us all feeling.
              </p>

              {currentUser && currentUserProfile ? (
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  <Link to={`/${currentUserProfile.username}`} className="btn btn-primary" style={{ fontSize: 15, padding: '12px 28px' }}>
                    My Profile
                  </Link>
                </div>
              ) : (
                <button
                  className="btn btn-primary"
                  style={{ fontSize: 15, padding: '12px 28px' }}
                  onClick={() => setAuthOpen(true)}
                >
                  Sign in / Sign up
                </button>
              )}
            </div>
          </div>
        </div>

        {/* The 8 slots visualization */}
        <div style={{ maxWidth: 680, margin: '0 auto', padding: '0 20px 80px' }}>
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)',
            gap: 10,
          }}>
            {EXAMPLES.map((ex, i) => (
              <div key={i} style={{
                background: 'var(--card)', border: '1px solid var(--border)',
                borderRadius: 'var(--radius)', padding: '14px 12px',
                position: 'relative',
              }}>
                <div style={{
                  width: '100%', paddingBottom: '100%', borderRadius: 6,
                  background: 'var(--card-2)', marginBottom: 8, position: 'relative',
                }}>
                  <div style={{
                    position: 'absolute', inset: 0, display: 'flex',
                    alignItems: 'center', justifyContent: 'center',
                  }}>
                    {i === 7
                      ? <img src="/yt8.png" alt="8" style={{ width: '50%', height: '50%', objectFit: 'contain', opacity: 0.5 }} />
                      : <span style={{ fontSize: 'clamp(34px, 5vw, 54px)', fontWeight: 900, color: 'var(--accent)', opacity: 0.5 }}>{i + 1}</span>
                    }
                  </div>
                </div>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text)', lineHeight: 1.3, marginBottom: 2 }}>
                  {ex.name}
                </div>
                <div style={{ fontSize: 10, color: 'var(--muted)', lineHeight: 1.3 }}>
                  {ex.sub}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Features */}
        <div style={{ borderTop: '1px solid var(--border)', background: 'var(--card)' }}>
          <div style={{ maxWidth: 680, margin: '0 auto', padding: '60px 20px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 36 }}>

              <div>
                <div style={{ width: 32, height: 3, background: 'var(--accent)', borderRadius: 2, marginBottom: 12 }} />
                <strong style={{ display: 'block', marginBottom: 6, fontSize: 14 }}>You find out when you move</strong>
                <p style={{ fontSize: 13, color: 'var(--muted)', margin: 0, lineHeight: 1.6 }}>
                  Get notified when someone adds you to their Top 8, and when you move up or down. You'll know exactly where you stand.
                </p>
              </div>

              <div>
                <div style={{ width: 32, height: 3, background: 'var(--accent)', borderRadius: 2, marginBottom: 12 }} />
                <strong style={{ display: 'block', marginBottom: 6, fontSize: 14 }}>Every move is a message</strong>
                <p style={{ fontSize: 13, color: 'var(--muted)', margin: 0, lineHeight: 1.6 }}>
                  Adding someone is a statement. Moving them up is a flex. Dropping them is a choice they'll notice. No likes, no comments. Just the list.
                </p>
              </div>

              <div>
                <div style={{ width: 32, height: 3, background: 'var(--accent)', borderRadius: 2, marginBottom: 12 }} />
                <strong style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6, fontSize: 14 }}>
                  Earn the 8 badge
                  <img src="/connected.png" alt="Connected badge" style={{ width: 18, height: 18, objectFit: 'contain', verticalAlign: 'middle' }} />
                </strong>
                <p style={{ fontSize: 13, color: 'var(--muted)', margin: 0, lineHeight: 1.6 }}>
                  When someone on Your Top 8 accepts your connection, the badge appears on their tile. It's mutual or it's nothing.
                </p>
              </div>

              <div>
                <div style={{ width: 32, height: 3, background: 'var(--accent)', borderRadius: 2, marginBottom: 12 }} />
                <strong style={{ display: 'block', marginBottom: 6, fontSize: 14 }}>Your people, your shortcuts</strong>
                <p style={{ fontSize: 13, color: 'var(--muted)', margin: 0, lineHeight: 1.6 }}>
                  Show who matters, then tap into the connections you use most. Each tile can link to a Your Top 8 profile, a social account, or a private text shortcut just for you.
                </p>
              </div>

            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        {!currentUser && (
          <div style={{ maxWidth: 680, margin: '0 auto', padding: '60px 20px', textAlign: 'center' }}>
            <h2 style={{ fontSize: 28, fontWeight: 800, margin: '0 0 12px', letterSpacing: '-0.5px' }}>
              Make your Top 8.
            </h2>
            <p style={{ color: 'var(--muted)', margin: '0 0 28px', fontSize: 15 }}>
              Free. No spam. Just the people who matter.
            </p>
            <button
              className="btn btn-primary"
              style={{ fontSize: 15, padding: '12px 32px' }}
              onClick={() => setAuthOpen(true)}
            >
              Get started
            </button>
          </div>
        )}

        <Footer />

      </div>

      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} onLogin={onLogin} />
    </>
  );
}

export default HomePage;
